import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import TurnstileWidget from "../components/TurnstileWidget";
// Photo de fond hébergée avec le site (14/10) : plus de requête vers images.pexels.com à chaque visite de cette page
// publique. Photo Pexels n° 13878094 (licence Pexels : usage libre, modification permise, mention non obligatoire),
// mêmes dimensions (867 × 1300), convertie en WebP : 32 ko au lieu de 133.
import fondConnexion from "../assets/connexion-fond.webp";

// Délai de grâce avant de débloquer le bouton sans jeton : sur un poste où
// le script Cloudflare est bloqué (pare-feu, bloqueur), le jeton n'arrivera
// jamais — sans ce repli, le client ne pourrait plus jamais se connecter.
// Le backend reste seul garant de la sécurité (il exige toujours le jeton
// après 2 échecs, cf. core.TURNSTILE_REQUIRED_AFTER_FAILURES) : ce délai ne
// fait qu'éviter un blocage indéfini côté UI.
const TURNSTILE_GRACE_PERIOD_MS = 4000;

// Turnstile tourne en mode Invisible (configuré côté dashboard Cloudflare,
// site key inchangée) : le widget est monté en permanence et envoie son
// jeton à chaque tentative de connexion, pas seulement après des échecs —
// le backend (core.TURNSTILE_REQUIRED_AFTER_FAILURES) reste seul juge de
// quand ce jeton est réellement exigé, mais l'a désormais toujours reçu.
// TURNSTILE_CONFIGURED évite de bloquer indéfiniment le bouton dans les
// environnements sans site key (dev local, CI) : TurnstileWidget ne rend
// rien dans ce cas (cf. son propre guard), donc turnstileToken resterait vide.
const TURNSTILE_CONFIGURED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Login() {
  const { login, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetTick, setTurnstileResetTick] = useState(0);
  const [turnstileTimedOut, setTurnstileTimedOut] = useState(false);
  const turnstileTimerRef = useRef(null);
  const [showPwd, setShowPwd] = useState(false);

  // Démarre le délai de grâce une seule fois, au montage. Nettoyé au
  // démontage (composant quitté avant l'échéance).
  useEffect(() => {
    if (!TURNSTILE_CONFIGURED) return;
    turnstileTimerRef.current = setTimeout(() => setTurnstileTimedOut(true), TURNSTILE_GRACE_PERIOD_MS);
    return () => clearTimeout(turnstileTimerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Nettoyé aussi dès qu'un jeton arrive : plus besoin d'attendre le délai.
  useEffect(() => {
    if (turnstileToken && turnstileTimerRef.current) {
      clearTimeout(turnstileTimerRef.current);
      turnstileTimerRef.current = null;
    }
  }, [turnstileToken]);

  const turnstileWaiting = TURNSTILE_CONFIGURED && !turnstileToken && !turnstileTimedOut;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password, turnstileToken);
      nav("/", { replace: true });  // « Retour » ne ramène pas au formulaire (recette du 15/10, ANO-02)
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (!e2.response) { console.error("Login network error:", e2.code, e2.message); setErr("Serveur momentanément indisponible. Veuillez réessayer dans un instant."); }
      else {
        // jeton Turnstile à usage unique : en redemander un nouveau pour la prochaine tentative
        setTurnstileResetTick((t) => t + 1);
        if (typeof d === "string") setErr(d);
        else setErr("Connexion impossible");
      }
    } finally { setBusy(false); }
  };

  // Déjà connecté : directement dans son espace, pas de formulaire (recette du 15/10, ANO-02).
  if (!loading && user) return <Navigate to="/" replace />;

  return (
    <main className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden bg-marine">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${fondConnexion})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-marine/80 via-marine/70 to-bordeaux/60" />
        <div className="relative z-10">
          <div className="font-display text-white text-4xl leading-none">L'Horizon</div>
          <div className="font-display text-white/90 text-5xl -mt-1">Metara</div>
        </div>
        <div className="relative z-10">
          <p className="font-display text-white text-4xl leading-tight max-w-md">
            L'espace premium qui relie Metara, ses entreprises et ses talents.
          </p>
          <p className="text-white/60 text-xs mt-6 uppercase tracking-[0.3em]">Metara Communication · Dubai</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 beige-texture">
        <form onSubmit={submit} className="w-full max-w-sm bg-sable rounded-2xl border border-marine/10 shadow-md p-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-bordeaux mb-2">Connexion</div>
          <h1 className="font-display text-4xl text-marine mb-8">Bienvenue</h1>
          {err && <div data-testid="login-error" className="mb-5 text-sm text-rouge bg-rouge-50 border border-rouge/30 rounded-xl px-3.5 py-2.5">{err}</div>}
          <label htmlFor="login-email" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Email</label>
          <input id="login-email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="inp mb-4" />
          <label htmlFor="login-password" className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">Mot de passe</label>
          <div className="relative mb-3">
            <input id="login-password" data-testid="login-password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="inp pr-10" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} data-testid="login-password-toggle"
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-marine hover:opacity-70">
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="text-right mb-6"><Link to="/mot-de-passe-oublie" data-testid="forgot-link" className="text-xs text-marine hover:text-bordeaux hover:underline">Mot de passe oublié ?</Link></div>
          {TURNSTILE_CONFIGURED && <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileResetTick} />}
          <button data-testid="login-submit" disabled={busy || turnstileWaiting}
            className="w-full bg-bordeaux text-white rounded-xl py-3 text-sm font-semibold hover:bg-bordeaux-hover transition-all active:scale-95 flex items-center justify-center gap-2">
            {(busy || turnstileWaiting) && <Loader2 size={16} className="animate-spin" />}
            {turnstileWaiting ? <span data-testid="turnstile-waiting">Vérification…</span> : "Se connecter"}
          </button>
          <p className="text-xs text-marine/80 mt-7 text-center">Accès sur invitation uniquement · Comptes créés par Metara</p>
        </form>
      </div>
    </main>
  );
}
